/* SpawnAnything — content gating: what the sandbox gives everyone, what comes out of crates.
   THE single gating table is CRATE_ITEMS below. Rule of thumb:
   - Everything that is NOT in CRATE_ITEMS is free (hand-made basics, most era units, buildings, worlds).
   - AI-generated units and wand/dice mutants (type keys starting with "u_") are always free.
   - A unit always brings its own standard weapon (e.g. the free Mech-Pilot keeps his railgun);
     a crate weapon unlocks equipping it on ANY unit.
   Pure data + lookups (no DOM). The crate screen (src/ui/crates.js) rolls from LOOT. */
import { TYPES, WEAPONS } from './voxel.js';
import { ERA_TOOLS, ERA_TYPES, ERA_WEAPONS } from '../content/eras.js';

// weights per rarity follow the classic 79.92 / 15.98 / 3.2 / 0.64 / 0.26 % split
export const RARITIES = [
  { key: 'selten', name: 'Selten', color: '#4b69ff', w: 7992 },
  { key: 'episch', name: 'Episch', color: '#8847ff', w: 1598 },
  { key: 'legendaer', name: 'Legendär', color: '#d32ce6', w: 320 },
  { key: 'mythisch', name: 'Mythisch', color: '#eb4b4b', w: 64 },
  { key: 'exotisch', name: '★ Exotisch', color: '#ffc93c', w: 26 },
];
export const RARITY = Object.fromEntries(RARITIES.map((r, i) => [r.key, { ...r, i }]));

const I = (kind, key, rarity, name) => ({ kind, key, rarity, name });
/** Crate-only content (kind, key, rarity). Edit here to move content between free and crates. */
export const CRATE_ITEMS = [
  // Selten: special weapons/units of the old eras (the units that normally carry them stay free)
  I('weapon', 'armbrust', 'selten'), I('weapon', 'muskete', 'selten'), I('weapon', 'lanze', 'selten'), I('weapon', 'hellebarde', 'selten'),
  I('weapon', 'wurfspeer', 'selten'), I('unit', 'hoplit', 'selten'), I('unit', 'streitwagen', 'selten'), I('unit', 'lanzenreiter', 'selten'),
  // Episch
  I('weapon', 'flammenwerfer', 'episch'), I('weapon', 'mg', 'episch'), I('weapon', 'sturmgewehr', 'episch'), I('weapon', 'raketenwerfer_modern', 'episch'),
  I('weapon', 'giftgaswerfer', 'episch'), I('unit', 'saebelzahntiger', 'episch'), I('unit', 'gladiator', 'episch'),
  // Legendär
  I('weapon', 'laserpistole', 'legendaer'), I('weapon', 'lasergewehr', 'legendaer'), I('weapon', 'plasmagewehr', 'legendaer'), I('weapon', 'minigun', 'legendaer'),
  I('unit', 'mammut', 'legendaer'), I('unit', 'robohund', 'legendaer'),
  // Mythisch: bosses and super weapons
  I('weapon', 'railgun', 'mythisch'), I('weapon', 'plasma', 'mythisch'), I('unit', 'trex', 'mythisch'), I('unit', 'mechhase', 'mythisch'), I('unit', 'optimus', 'mythisch'),
  // ★ Exotisch
  I('tool', 'atom', 'exotisch', 'Atombombe'), I('tool', 'orbitallaser', 'exotisch', 'Orbitallaser'), I('unit', 'weltraumwurm', 'exotisch'), I('unit', 'megatron', 'exotisch'),
];

/** Documented free baseline (never gated; checked by tests). Not exhaustive: anything not in CRATE_ITEMS is free. */
export const FREE_BASELINE = {
  unit: ['ritter', 'legionaer', 'soldat', 'zombie', 'priester', 'trump', 'megamind', 'pharao', 'huhn', 'panzer', 'seemonster'],
  weapon: ['faeuste', 'schwert', 'gewehr', 'raketen', 'kanone', 'huhnkanone', 'keule', 'steinwurf', 'speer', 'bogen', 'stosszaehne', 'pranke', 'karabiner', 'pistole', 'scharfschuetzengewehr'],
  tool: ['hand', 'granate', 'fass', 'meteor', 'katapult', 'artillerie', 'stinkwolke'],
};

const ALL_TYPES = { ...TYPES, ...ERA_TYPES }, ALL_WEAPONS = { ...WEAPONS, ...ERA_WEAPONS };
const TOOL_NAMES = { granate: 'Granate', fass: 'Sprengfass', atom: 'Atombombe', ...Object.fromEntries(Object.entries(ERA_TOOLS).map(([k, v]) => [k, v.name])) };
export const KIND_NAME = { weapon: 'Waffe', unit: 'Einheit', tool: 'Werkzeug' };

export const itemId = (kind, key) => `${kind}:${key}`;
const exists = it => it.kind === 'tool' ? !!TOOL_NAMES[it.key] : it.kind === 'weapon' ? !!ALL_WEAPONS[it.key] : !!ALL_TYPES[it.key];
/** Crate pool: gating table entries whose content exists, with display names. */
export const LOOT = CRATE_ITEMS.filter(exists).map(it => ({
  ...it, id: itemId(it.kind, it.key),
  name: it.name || (it.kind === 'weapon' ? ALL_WEAPONS[it.key].name : it.kind === 'unit' ? ALL_TYPES[it.key].name : TOOL_NAMES[it.key]),
}));
const BY_ID = new Map(LOOT.map(it => [it.id, it]));

/** The crate item gating this content, or null when it is free. */
export function gateOf(kind, key) {
  if (!key || (kind === 'unit' && String(key).startsWith('u_'))) return null; // AI-generated + mutants: always free
  return BY_ID.get(itemId(kind, key)) || null;
}
/** Is this content usable for a player with inventory `inv` (anything with isUnlocked(kind, key))? */
export function isAvailable(inv, kind, key) {
  const g = gateOf(kind, key);
  return !g || !!inv?.isUnlocked(kind, key);
}
/** Weapon check for a unit: crate weapons need an unlock unless they are the unit's own standard weapon. */
export function weaponAvailable(inv, weaponKey, typeKey, types = ALL_TYPES) {
  return types[typeKey]?.weapon === weaponKey || isAvailable(inv, 'weapon', weaponKey);
}
export const findLoot = (kind, key) => BY_ID.get(itemId(kind, key)) || null;

/** Odds per rarity (bonus multiplies the two top tiers, e.g. the supply drop) incl. per-item chance. */
export function oddsTable(bonus = 1) {
  const ws = RARITIES.map((r, i) => r.w * (i >= 3 ? bonus : 1)), sum = ws.reduce((a, b) => a + b, 0);
  return RARITIES.map((r, i) => { const n = LOOT.filter(it => it.rarity === r.key).length; return { ...r, p: ws[i] / sum, n, each: n ? ws[i] / sum / n : 0 }; });
}
/** Pick a rarity by weight, then an item of that rarity. Client-side for the demo; a server would own this roll. */
export function rollLoot(rand = Math.random, bonus = 1) {
  const ws = RARITIES.map((r, i) => r.w * (i >= 3 ? bonus : 1));
  let x = rand() * ws.reduce((a, b) => a + b, 0), ri = 0;
  for (; ri < ws.length - 1; ri++) { x -= ws[ri]; if (x < 0) break; }
  const pool = LOOT.filter(it => it.rarity === RARITIES[ri].key);
  return pool[Math.floor(rand() * pool.length) % pool.length];
}
